import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, BorderStyle, ImageRun, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

type AnyObject = Record<string, any>;

async function fetchImageWithDims(url: string): Promise<{ buffer: Uint8Array; width: number; height: number } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const arrayBuffer = await res.arrayBuffer();
    const blob = new Blob([arrayBuffer]);
    const objectUrl = URL.createObjectURL(blob);

    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => reject(new Error('Image load error'));
      img.src = objectUrl;
    }).finally(() => URL.revokeObjectURL(objectUrl));

    return { buffer: new Uint8Array(arrayBuffer), width: dims.w, height: dims.h };
  } catch (err) {
    console.warn('DocumentExporter: failed to fetch image', url, err);
    return null;
  }
}

function discoverImageUrl(item: AnyObject): string | null {
  const candidates = [
    item?.image,
    item?.imageUrl,
    item?.textandimage?.image,
    item?.textandimage?.imageUrl,
    item?.chart?.image,
    item?.table?.image,
  ];
  for (const c of candidates) {
    if (!c) continue;
    if (typeof c === 'string' && c.trim()) return c;
    if (typeof c === 'object' && typeof c.url === 'string') return c.url;
    
    // Handle Image component structure with imageUrl1, imageUrl2, etc.
    if (typeof c === 'object') {
      const imageUrls = ['imageUrl1', 'imageUrl2', 'imageUrl3', 'imageUrl4'];
      for (const urlKey of imageUrls) {
        const url = c[urlKey];
        if (url && typeof url === 'string' && url.trim() && url !== 'null' && url !== 'undefined') {
          return url; // Return the first valid image URL found
        }
      }
    }
  }
  return null;
}

const MAX_IMAGE_WIDTH = 600;

async function renderChartToPng(chartObj: AnyObject): Promise<Uint8Array | null> {
  try {
    // find first numeric series array in chartObj
    let labels: string[] = [];
    let series: number[] | null = null;
    let title = '';
    if (!chartObj) return null;
    // common shapes: { labels: [], series: [..] } or { series1: [], series2: [] } or { series: [{name, data}] }
    if (Array.isArray(chartObj.labels)) labels = chartObj.labels.map(String);
    if (Array.isArray(chartObj.series) && chartObj.series.length && typeof chartObj.series[0] === 'number') {
      series = chartObj.series as number[];
    } else if (Array.isArray(chartObj.series) && chartObj.series.length && typeof chartObj.series[0] === 'object' && Array.isArray(chartObj.series[0].data)) {
      series = chartObj.series[0].data.map((v: any) => Number(v || 0));
      title = chartObj.series[0].name || title;
    } else {
      // check for series1, series2... or legacy objects where each series is an object with key and value1..valueN
      for (const k of Object.keys(chartObj)) {
        const v = chartObj[k];
        if (Array.isArray(v) && v.every((x: any) => typeof x === 'number' || !isNaN(Number(x)))) {
          series = v.map((x: any) => Number(x));
          break;
        }
        // legacy series objects like { series1: { key: 'Early Stage', value1: '60', value2: null, ... }, series2: {...} }
        if (v && typeof v === 'object' && ('value1' in v || 'value' in v || 'key' in v)) {
          // collect keys as labels and the main numeric field as values
          const extractedLabels: string[] = [];
          const extractedValues: number[] = [];
          // iterate series1..seriesN
          for (let idx = 1; idx <= 20; idx++) {
            const seriesKey = `series${idx}`;
            const s = chartObj[seriesKey];
            if (!s || typeof s !== 'object') continue;
            // prefer value1 or value
            let val: any = null;
            if ('value1' in s) {
              val = s['value1'];
            } else if ('value' in s) {
              val = s['value'];
            } else if ('value0' in s) {
              val = s['value0'];
            }
            const keylbl = s['key'] || s['label'] || `series${idx}`;
            extractedLabels.push(String(keylbl || `s${idx}`));
            extractedValues.push(Number(val || 0));
          }
          if (extractedValues.length) {
            labels = extractedLabels;
            series = extractedValues;
            break;
          }
        }
      }
    }
    if (!series || !series.length) return null;

    const width = Math.min(MAX_IMAGE_WIDTH, 900);
    const height = 360;
    // create offscreen canvas
    const canvas = (typeof document !== 'undefined') ? document.createElement('canvas') : null;
    if (!canvas) return null;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    // title
    const chartTitle = chartObj.title || title || '';
    ctx.fillStyle = '#222222';
    ctx.font = '20px sans-serif';
    ctx.fillText(chartTitle, 14, 28);

    // chart area margins
    const margin = { top: 40, right: 20, bottom: 40, left: 60 };
    const cw = width - margin.left - margin.right;
    const ch = height - margin.top - margin.bottom;

    const maxVal = Math.max(...series.map(x => Number(x || 0)), 1);
    const barWidth = cw / series.length * 0.6;

    // draw y grid lines
    ctx.strokeStyle = '#e6e6e6';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i <= 5; i++) {
      const y = margin.top + (ch * i) / 5;
      ctx.moveTo(margin.left, y);
      ctx.lineTo(width - margin.right, y);
    }
    ctx.stroke();

    // draw bars
    for (let i = 0; i < series.length; i++) {
      const val = Number(series[i] || 0);
      const x = margin.left + i * (cw / series.length) + ((cw / series.length) - barWidth) / 2;
      const h = (val / maxVal) * ch;
      const y = margin.top + ch - h;
      ctx.fillStyle = '#aee3e0';
      ctx.fillRect(x, y, barWidth, h);
      // label
      ctx.fillStyle = '#333';
      ctx.font = '12px sans-serif';
      const lbl = labels[i] || String(i + 1);
      ctx.fillText(lbl, x, margin.top + ch + 16);
    }

    // legend / footer small note
    ctx.fillStyle = '#666';
    ctx.font = '12px sans-serif';
    ctx.fillText('Generated chart', width - margin.right - 110, height - 8);

    // export to PNG
    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    const arrayBuffer = await blob.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (err) {
    console.warn('renderChartToPng failed', err);
    return null;
  }
}

const processHeadings = (text: string) => {
  if (!text) return { text: '', level: undefined } as any;
  if (text.startsWith('# ')) return { text: text.substring(2), level: HeadingLevel.HEADING_1 } as any;
  if (text.startsWith('## ')) return { text: text.substring(3), level: HeadingLevel.HEADING_2 } as any;
  if (text.startsWith('### ')) return { text: text.substring(4), level: HeadingLevel.HEADING_3 } as any;
  return { text, level: undefined } as any;
};

const processBulletPoints = (text: string, children: any[] = []) => {
  const lines = (text || '').split('\n');
  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) {
      children.push(new Paragraph({}));
      return;
    }
    if (trimmed.startsWith('- ') || /^\d+\./.test(trimmed)) {
      const bulletText = trimmed.startsWith('- ') ? trimmed.substring(2) : trimmed.replace(/^\d+\.\s*/, '');
      children.push(new Paragraph({ bullet: { level: 0 }, children: [new TextRun(bulletText)] }));
    } else if (trimmed.startsWith('#')) {
      const { text, level } = processHeadings(trimmed);
      children.push(new Paragraph({ text, heading: level }));
    } else {
      children.push(new Paragraph({ children: [new TextRun(trimmed)] }));
    }
  });
  return children;
};

const processTable = (tableData: AnyObject) => {
  if (!tableData) return null;
  const rows: any[] = [];

  // Normalize possible shapes: { columnHeaders: [], rows: [] } OR legacy structured fields
  let columnHeaders: string[] = [];
  let directRows: any[] = [];

  if (Array.isArray(tableData.columnHeaders) && tableData.columnHeaders.length) {
    columnHeaders = tableData.columnHeaders;
  }

  if (Array.isArray(tableData.rows) && tableData.rows.length) {
    directRows = tableData.rows;
  }

  // Legacy fields: columnHeader1..5 and rows1..8 with attribute1..attribute5
  if (!columnHeaders.length) {
    const headers: string[] = [];
    for (let i = 1; i <= 5; i++) {
      const key = `columnHeader${i}`;
      if (tableData[key]) headers.push(String(tableData[key]));
    }
    if (headers.length) columnHeaders = headers;
  }

  if (!directRows.length) {
    // Rows1..rows8 each having attribute1..attribute5
    for (let r = 1; r <= 8; r++) {
      const rowObj = tableData[`rows${r}`];
      if (rowObj && typeof rowObj === 'object') {
        const rowArr: any[] = [];
        for (let c = 1; c <= 5; c++) {
          const a = rowObj[`attribute${c}`];
          rowArr.push(a != null ? String(a) : '');
        }
        directRows.push(rowArr);
      }
    }
  }

  if (columnHeaders.length) {
    const headerCells = columnHeaders.map((h: string) =>
      new TableCell({
        children: [
          new Paragraph({
            children: [new TextRun({ text: h || '', bold: true })],
            alignment: AlignmentType.CENTER,
          }),
        ],
        shading: { fill: 'F2F2F2' },
        width: { size: Math.floor(100 / columnHeaders.length), type: 'pct' },
        margins: { top: 100, bottom: 100, left: 100, right: 100 },
      })
    );
    rows.push(new TableRow({ children: headerCells }));
  }

  if (directRows.length) {
    directRows.forEach((r: any[]) => {
      const cells = (r || []).map((c: any, idx: number) => {
        const text = String(c || '');
        const run = new TextRun({ text, bold: idx === 0 });
        return new TableCell({
          children: [new Paragraph({ children: [run], alignment: AlignmentType.LEFT })],
          width: { size: Math.floor(100 / columnHeaders.length), type: 'pct' },
          margins: { top: 80, bottom: 80, left: 120, right: 120 },
        });
      });
      rows.push(new TableRow({ children: cells }));
    });
  }

  if (!rows.length) return null;

  // Ensure table borders and alignment are consistent
  const table = new Table({
    rows,
    width: { size: 100, type: 'pct' },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1 },
      bottom: { style: BorderStyle.SINGLE, size: 1 },
      left: { style: BorderStyle.SINGLE, size: 1 },
      right: { style: BorderStyle.SINGLE, size: 1 },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
      insideVertical: { style: BorderStyle.SINGLE, size: 1 },
    },
    alignment: AlignmentType.CENTER,
  });

  return {
    table,
    source: tableData?.source || tableData?.attribution || tableData?.footer || null,
  };
};

export const exportToWord = async (
  outlines: AnyObject[],
  generatedData: Record<string, AnyObject[]>,
  documentName = 'Presentation'
) => {
  try {
  console.log('DocumentExporter: preparing export, outlines=', outlines?.length, 'generatedDataKeys=', Object.keys(generatedData || {}).length);
    const docContent: any[] = [];

    docContent.push(new Paragraph({ text: documentName, heading: HeadingLevel.TITLE, spacing: { after: 200 } }));
    docContent.push(new Paragraph({ text: 'Table of Contents', heading: HeadingLevel.HEADING_1 }));
    outlines.forEach(o => docContent.push(new Paragraph({ text: o?.title || o?.outlineID || 'Untitled', bullet: { level: 0 } })));
    docContent.push(new Paragraph({ children: [new TextRun({ text: '─'.repeat(50) })] }));

    for (const outline of outlines) {
      docContent.push(new Paragraph({ text: outline?.title || outline?.outlineID || 'Untitled', heading: HeadingLevel.HEADING_1, pageBreakBefore: true }));
      const sectionData = generatedData?.[outline.outlineID] || generatedData?.[outline.documentID] || generatedData?.[String(outline.outlineID)] || [];

  console.log('DocumentExporter: outline', outline?.outlineID, 'items=', sectionData?.length || 0);

      for (const item of sectionData) {
        const type = item?.component_type || '';
        console.log('DocumentExporter: Processing item type:', type, 'item:', item);
        
        if (type === 'Text') {
          const text = typeof item.text === 'object' ? item.text.text : (item.text || '');
          if (text) processBulletPoints(text, docContent);
        } else if (type === 'Image') {
          // Handle pure Image components
          console.log('DocumentExporter: Processing Image component:', item);
          const imageTitle = item?.image?.title || 'Image';
          if (imageTitle && imageTitle !== 'Image') {
            docContent.push(new Paragraph({ text: imageTitle, heading: HeadingLevel.HEADING_2 }));
          }
          
          // Handle multiple image URLs for Image components
          const imageData = item?.image;
          const imageUrls = [];
          
          if (typeof imageData === 'string' && imageData.trim()) {
            // Single image URL as string
            imageUrls.push(imageData);
          } else if (typeof imageData === 'object' && imageData) {
            // Multiple images object with imageUrl1, imageUrl2, etc.
            ['imageUrl1', 'imageUrl2', 'imageUrl3', 'imageUrl4'].forEach(key => {
              const url = imageData[key];
              if (url && typeof url === 'string' && url.trim() && url !== 'null' && url !== 'undefined') {
                imageUrls.push(url);
              }
            });
          }
          
          console.log('DocumentExporter: Found image URLs:', imageUrls);
          
          // Process each image URL
          for (const url of imageUrls) {
            try {
              console.log('DocumentExporter: Fetching image:', url);
              const fetched = await fetchImageWithDims(url);
              if (fetched) {
                const scale = fetched.width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / fetched.width : 1;
                const w = Math.round(fetched.width * scale);
                const h = Math.round(fetched.height * scale);
                console.log('DocumentExporter: Adding image to document:', { width: w, height: h });
                docContent.push(new Paragraph({ 
                  children: [new ImageRun(({ data: fetched.buffer, transformation: { width: w, height: h } }) as any)], 
                  spacing: { before: 200, after: 200 },
                  alignment: AlignmentType.CENTER 
                }));
              } else {
                console.warn('DocumentExporter: Failed to fetch image:', url);
                docContent.push(new Paragraph({ text: `[Image could not be loaded: ${url}]` }));
              }
            } catch (error) {
              console.warn('Error processing image:', url, error);
              docContent.push(new Paragraph({ text: `[Error loading image: ${url}]` }));
            }
          }
          
          // Add citation if present
          if (imageData?.citation) {
            docContent.push(new Paragraph({ 
              text: `Source: ${imageData.citation}`, 
              spacing: { before: 100, after: 100 }, 
              children: [new TextRun({ text: `Source: ${imageData.citation}`, italics: true, size: 18 })] 
            }));
          }
        } else if (type === 'TextandImage') {
          const title = item?.textandimage?.title || item?.component_type;
          if (title && title !== (outline?.title || outline?.outlineID || 'Untitled')) {
            docContent.push(new Paragraph({ text: title, heading: HeadingLevel.HEADING_2 }));
          }
          const body = item?.textandimage?.text || (typeof item.text === 'string' ? item.text : '');
          if (body) processBulletPoints(body, docContent);
          const url = discoverImageUrl(item);
          if (url) {
            const fetched = await fetchImageWithDims(url);
            if (fetched) {
              const scale = fetched.width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / fetched.width : 1;
              const w = Math.round(fetched.width * scale);
              const h = Math.round(fetched.height * scale);
              docContent.push(new Paragraph({ children: [new ImageRun(( { data: fetched.buffer, transformation: { width: w, height: h } } ) as any)], spacing: { before: 200, after: 200 } }));
            } else {
              docContent.push(new Paragraph({ text: `[Image could not be loaded: ${url}]` }));
            }
          }
        } else if (type === 'Table') {
          const tableTitle = item?.table?.title;
          if (tableTitle && tableTitle !== (outline?.title || outline?.outlineID || 'Untitled')) {
            docContent.push(new Paragraph({ text: tableTitle, heading: HeadingLevel.HEADING_2 }));
          }
          const tableResult = processTable(item.table);
          if (tableResult) {
            docContent.push(tableResult.table);
            if (tableResult.source) {
              docContent.push(new Paragraph({ text: `Source: ${tableResult.source}`, spacing: { before: 100, after: 100 }, children: [new TextRun({ text: `Source: ${tableResult.source}`, italics: true, size: 18 })] }));
            }
          }
        } else if (type === 'Chart') {
          const chartTitle = item?.chart?.title;
          if (chartTitle && chartTitle !== (outline?.title || outline?.outlineID || 'Untitled')) {
            docContent.push(new Paragraph({ text: chartTitle, heading: HeadingLevel.HEADING_2 }));
          }
          if (item?.chart?.description) docContent.push(new Paragraph({ text: item.chart.description }));
          const url = discoverImageUrl(item);
          if (url) {
            const fetched = await fetchImageWithDims(url);
            if (fetched) {
              const scale = fetched.width > MAX_IMAGE_WIDTH ? MAX_IMAGE_WIDTH / fetched.width : 1;
              const w = Math.round(fetched.width * scale);
              const h = Math.round(fetched.height * scale);
              docContent.push(new Paragraph({ children: [new ImageRun(( { data: fetched.buffer, transformation: { width: w, height: h } } ) as any)], spacing: { before: 100, after: 100 } }));
            } else {
              docContent.push(new Paragraph({ text: `[Chart image could not be loaded: ${url}]` }));
            }
          } else {
            // PATCH: Render chart as table with multiple series and labels
            const chartObj = item?.chart?.chart || item?.chart || item?.graph;
            let tableRows = [];
            let seriesNames = [];
            let labels = [];
            let seriesData = [];
            // Support react-chartjs-2 shape: { labels: [], datasets: [{ label, data }] }
            if (chartObj && Array.isArray(chartObj.labels) && Array.isArray(chartObj.datasets)) {
              labels = chartObj.labels.map(String);
              seriesNames = chartObj.datasets.map((ds: any) => ds.label || '');
              seriesData = chartObj.datasets.map((ds: any) => Array.isArray(ds.data) ? ds.data.map(Number) : []);
            } else if (chartObj && Array.isArray(chartObj.labels) && Array.isArray(chartObj.data)) {
              labels = chartObj.labels.map(String);
              seriesNames = [chartObj.title || 'Series'];
              seriesData = [chartObj.data.map(Number)];
            } else if (chartObj && Array.isArray(chartObj.series) && chartObj.series.length && typeof chartObj.series[0] === 'object' && Array.isArray(chartObj.series[0].data)) {
              labels = chartObj.labels ? chartObj.labels.map(String) : [];
              seriesNames = chartObj.series.map((s: any) => s.name || '');
              seriesData = chartObj.series.map((s: any) => Array.isArray(s.data) ? s.data.map(Number) : []);
            }
            // If we have valid series and labels, build table
            if (labels.length && seriesNames.length && seriesData.length) {
              // Table header: first cell empty, then series names
              const headerCells = [
                new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: '', bold: true })] })], shading: { fill: 'F2F2F2' } }),
                ...seriesNames.map((sn: any) =>
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: sn, bold: true })] })], shading: { fill: 'F2F2F2' } })
                )
              ];
              tableRows.push(new TableRow({ children: headerCells }));
              // Table rows: label, then value for each series
              for (let i = 0; i < labels.length; i++) {
                const rowCells = [
                  new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: labels[i], bold: true })] })] }),
                  ...seriesData.map((sd: any) =>
                    new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: sd[i] != null ? String(sd[i]) : '' })] })] })
                  )
                ];
                tableRows.push(new TableRow({ children: rowCells }));
              }
              docContent.push(new Table({ rows: tableRows, width: { size: 100, type: 'pct' } }));
            } else {
              // fallback: try to serialize numeric series from chartObj
              let serialized = false;
              if (chartObj && typeof chartObj === 'object') {
                for (const k of Object.keys(chartObj)) {
                  const v = chartObj[k];
                  if (Array.isArray(v)) {
                    docContent.push(new Paragraph({ text: `${k}: ${v.join(', ')}` }));
                    serialized = true;
                  } else if (v && typeof v === 'object' && Array.isArray(v.data)) {
                    docContent.push(new Paragraph({ text: `${k} (${v.name || ''}): ${v.data.join(', ')}` }));
                    serialized = true;
                  }
                }
              }
              if (!serialized) {
                const pngBuf = await renderChartToPng(chartObj);
                if (pngBuf) {
                  const fetchedBuf = pngBuf;
                  const w = Math.min(MAX_IMAGE_WIDTH, 600);
                  const h = 360;
                  docContent.push(new Paragraph({ children: [new ImageRun(( { data: fetchedBuf, transformation: { width: w, height: h } } ) as any)], spacing: { before: 100, after: 100 } }));
                } else {
                  docContent.push(new Paragraph({ text: '[Chart visualization — rendering failed]' }));
                  docContent.push(new Paragraph({ text: `Debug: chart keys = ${Object.keys(chartObj || {}).join(', ')}` }));
                }
              }
            }
          }
        } else if (type === 'People') {
          docContent.push(new Paragraph({ text: item?.people?.title || 'Team', heading: HeadingLevel.HEADING_2 }));
          // normalize legacy people shape: name1..6, designation1..6, description1..6
          const peopleArr: AnyObject[] = [];
          if (Array.isArray(item?.people?.people) && item.people.people.length) {
            item.people.people.forEach((p: AnyObject) => peopleArr.push(p));
          } else if (item?.people && typeof item.people === 'object') {
            for (let i = 1; i <= 8; i++) {
              const name = item.people[`name${i}`] || item.people[`personName${i}`];
              if (name) {
                peopleArr.push({
                  name: String(name),
                  title: item.people[`designation${i}`] || item.people[`title${i}`] || item.people[`role${i}`] || '',
                  description: item.people[`description${i}`] || item.people[`desc${i}`] || ''
                });
              }
            }
          }
          if (peopleArr.length) {
            // Render each person with clear spacing and styles: name (bold), title (italic), description (normal)
            peopleArr.forEach((p: AnyObject) => {
              const name = String(p?.name || '');
              const title = p?.title || p?.designation || '';
              const description = p?.description || p?.desc || p?.details || '';

              // Name (bold, slightly larger)
              const nameRun = new TextRun({ text: name, bold: true, size: 28 });
              docContent.push(new Paragraph({ children: [nameRun], spacing: { before: 100, after: 80 } }));

              // Title / Role (italic)
              if (title) {
                const titleRun = new TextRun({ text: String(title), italics: true, size: 24 });
                docContent.push(new Paragraph({ children: [titleRun], spacing: { after: 80 } }));
              }

              // Description (regular)
              if (description) {
                // Allow multi-line descriptions
                const descLines = String(description).split('\n');
                descLines.forEach((line) => {
                  docContent.push(new Paragraph({ children: [new TextRun({ text: line, size: 22 })] }));
                });
              }

              // Spacer between people
              docContent.push(new Paragraph({}));
            });
          }
        } else {
          const raw = item?.text?.text || item?.text || '';
          if (raw) processBulletPoints(raw, docContent);
        }
      }
    }

  // create document with the built content
  const doc = new Document({ sections: [{ properties: {}, children: docContent }] });
  const blob = await Packer.toBlob(doc);
    console.log('DocumentExporter: generated blob, size=', blob.size, 'bytes, filename=', `${documentName}.docx`);
    try {
      saveAs(blob, `${documentName}.docx`);
      console.log('DocumentExporter: saveAs called');
    } catch (err) {
      console.warn('DocumentExporter: saveAs failed', err);
    }
    return true;
  } catch (err) {
    console.error('DocumentExporter.exportToWord error', err);
    return false;
  }
};

export default exportToWord;
